import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import { colors } from "./colors";
import { getElevations } from "./elevations";
export var reactions = ["normal", "hover", "pressed", "focused", "disabled"];
export var reactionTypes = ["color", "elevation", "colorAndElevation"];
var overlayMap = {
  white: {
    50: colors.whiteAlpha50,
    100: colors.whiteAlpha100
  },
  black: {
    50: colors.blackAlpha50,
    100: colors.blackAlpha100
  }
};
/**
 * 色と Elevation によるリアクション表現を返す。ボタンなどの比較的小さいコンポーネントで利用する。
 * 基準となる Elevation が null の場合、Elevation は変化しなくなる。
 *
 * @private
 */

export var getReactionsByColorAndElevation = function getReactionsByColorAndElevation(p) {
  return {
    normal: {
      overlays: [],
      shadows: []
    },
    hover: {
      overlays: [overlayMap[findOverlayTypeByReaction(p.overlayType, "hover")]["50"]],
      shadows: getElevations(p.baseElevation === null ? null : p.baseElevation + 4, {
        color: p.elevationColor
      })
    },
    pressed: {
      overlays: [overlayMap[findOverlayTypeByReaction(p.overlayType, "pressed")]["100"]],
      shadows: getElevations(p.baseElevation === null ? null : p.baseElevation + 4, {
        color: p.elevationColor
      })
    },
    focused: {
      overlays: [overlayMap[findOverlayTypeByReaction(p.overlayType, "focused")]["50"]],
      shadows: getElevations(p.baseElevation === null ? null : p.baseElevation + 4, {
        color: p.elevationColor
      })
    },
    disabled: {
      overlays: [overlayMap[findOverlayTypeByReaction(p.overlayType, "disabled")]["100"]],
      shadows: []
    }
  };
};
/**
 * 色によるリアクション表現を返す。Translucent な見た目のコンポーネントで利用する。
 * 基準となる Elevation が null の場合、Elevation は変化しなくなる。
 *
 * @private
 */

export var getReactionsByElevation = function getReactionsByElevation(p) {
  return {
    normal: {
      overlays: [],
      shadows: []
    },
    hover: {
      overlays: [],
      shadows: getElevations(p.baseElevation === null ? null : p.baseElevation + 4, {
        color: p.elevationColor
      })
    },
    pressed: {
      overlays: [overlayMap[findOverlayTypeByReaction(p.overlayType, "pressed")]["50"]],
      shadows: getElevations(p.baseElevation === null ? null : p.baseElevation + 4, {
        color: p.elevationColor
      })
    },
    focused: {
      overlays: [],
      shadows: getElevations(p.baseElevation === null ? null : p.baseElevation + 4, {
        color: p.elevationColor
      })
    },
    disabled: {
      overlays: p.backwardCompatForUiReactLegacy ? [] : [overlayMap[findOverlayTypeByReaction(p.overlayType, "disabled")]["50"]],
      shadows: []
    }
  };
};
/**
 * 色によるリアクション表現を返す。Translucent な見た目のコンポーネントで利用する。
 *
 * @private
 */

export var getReactionsByColor = function getReactionsByColor(p) {
  return {
    normal: {
      overlays: [],
      shadows: []
    },
    hover: {
      overlays: [overlayMap[findOverlayTypeByReaction(p.overlayType, "hover")]["50"]],
      shadows: []
    },
    pressed: {
      overlays: [overlayMap[findOverlayTypeByReaction(p.overlayType, "pressed")]["100"]],
      shadows: []
    },
    focused: {
      overlays: [overlayMap[findOverlayTypeByReaction(p.overlayType, "focused")]["50"]],
      shadows: []
    },
    disabled: {
      overlays: p.backwardCompatForUiReactLegacy ? [] : [overlayMap[findOverlayTypeByReaction(p.overlayType, "disabled")]["50"]],
      shadows: []
    }
  };
};
/**
 * @private
 */

export var createOverlayedBackground = function createOverlayedBackground(base) {
  for (var _len = arguments.length, overlays = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    overlays[_key - 1] = arguments[_key];
  }

  return [].concat(_toConsumableArray(overlays.map(toGradient).reverse()), [toGradient(base)]);
};
/**
 * @private
 */

export var toGradient = function toGradient(c) {
  return c.startsWith("linear-gradient(") ? c : "linear-gradient(".concat(c, ", ").concat(c, ")");
};
export var createReactionStylesMap = function createReactionStylesMap(p) {
  var paramsList = getReactionParamsList(p);

  var createStyle = function createStyle(r) {
    var params = paramsList[r];

    if (r === "disabled" && !p.backwardCompatForUiReactLegacy) {
      return {
        backgroundImage: createOverlayedBackground.apply(void 0, ["transparent"].concat(_toConsumableArray(params.overlays))).join(","),
        boxShadow: "none",
        // NOTE: ui-react-2022 以後で background-blend-mode を使った overlay 対応のために利用する
        // params.overlays は必ず1つしかないので、先頭だけ利用する
        background: params.overlays.length > 0 ? "".concat(params.overlays[0], " ").concat(toGradient("transparent")) : toGradient("transparent")
      };
    }

    return {
      backgroundImage: createOverlayedBackground.apply(void 0, [p.background].concat(_toConsumableArray(params.overlays))).join(","),
      boxShadow: r === "disabled" ? "none" : params.shadows.join(","),
      // NOTE: ui-react-2022 以後で background-blend-mode を使った overlay 対応のために利用する
      // params.overlays は必ず1つしかないので、先頭だけ利用する
      background: params.overlays.length > 0 ? "".concat(params.overlays[0], " ").concat(toGradient(p.background)) : toGradient(p.background)
    };
  };

  var map = {
    normal: createStyle("normal"),
    hover: createStyle("hover"),
    pressed: createStyle("pressed"),
    focused: createStyle("focused"),
    disabled: createStyle("disabled")
  };
  return map;
};

var getReactionParamsList = function getReactionParamsList(p) {
  switch (p.reactionType) {
    case "color":
      return getReactionsByColor(p);

    case "elevation":
      return getReactionsByElevation(p);

    case "colorAndElevation":
      return getReactionsByColorAndElevation(p);
  }
};

function findOverlayTypeByReaction(overlayType, reaction) {
  if (typeof overlayType === "string") return overlayType;
  return overlayType[reaction];
}
//# sourceMappingURL=reactions.js.map